import {
  Building2,
  FileText,
  FolderOpen,
  Mail,
  Paperclip,
  User,
  type LucideIcon,
} from 'lucide-react';
import type { SearchGroup, SearchResult } from '../../lib/search-types';
import { SearchSnippet } from './search-snippet';

const GROUP_ICONS: Record<SearchGroup, LucideIcon> = {
  Saker: FolderOpen,
  Hendelser: Mail,
  Dokumenter: FileText,
  Personer: User,
  Organisasjoner: Building2,
};

interface TopBarSearchResultItemProps {
  item: SearchResult;
  isActive: boolean;
  onSelect: (result: SearchResult) => void;
  onHover: () => void;
}

function AttachmentRow({ names, hasAttachments }: { names?: string[]; hasAttachments?: boolean }) {
  if (!hasAttachments && (!names || names.length === 0)) return null;
  const visible = (names ?? []).slice(0, 3);
  const rest = (names?.length ?? 0) - visible.length;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <Paperclip size={10} className="shrink-0 text-orange-300/80" />
      {visible.length > 0 ? (
        visible.map((name) => (
          <span
            key={name}
            className="max-w-[9rem] truncate rounded border px-1.5 py-0.5 text-[10px]"
            style={{
              borderColor: 'rgba(251,146,60,0.35)',
              background: 'rgba(251,146,60,0.12)',
              color: '#fdba74',
            }}
            title={name}
          >
            {name}
          </span>
        ))
      ) : (
        <span className="text-[10px] text-orange-300/80">Har vedlegg</span>
      )}
      {rest > 0 && <span className="text-[10px] text-text-tertiary">+{rest}</span>}
    </div>
  );
}

export function TopBarSearchResultItem({
  item,
  isActive,
  onSelect,
  onHover,
}: TopBarSearchResultItemProps) {
  const Icon = GROUP_ICONS[item.group];

  return (
    <button
      role="option"
      aria-selected={isActive}
      className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors duration-75 ${
        isActive ? 'bg-accent/12' : 'hover:bg-white/[0.04]'
      }`}
      onMouseEnter={onHover}
      onClick={() => onSelect(item)}
    >
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
          isActive ? 'bg-accent/20 text-accent' : 'bg-white/[0.05] text-text-secondary'
        }`}
      >
        <Icon size={13} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-xs font-medium text-text-primary">{item.title}</span>
          {item.subtitle && (
            <span className="shrink-0 text-[10px] text-text-tertiary">{item.subtitle}</span>
          )}
        </span>

        {(item.metaLabel || item.metaDetail) && (
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-text-secondary">
            {item.metaLabel && (
              <span
                className="rounded px-1 py-px uppercase tracking-wide text-text-tertiary"
                style={{ background: 'rgba(255,255,255,0.06)' }}
              >
                {item.metaLabel}
              </span>
            )}
            {item.metaDetail && <span className="truncate">{item.metaDetail}</span>}
          </span>
        )}

        {item.snippet && <SearchSnippet html={item.snippet} />}
        <AttachmentRow names={item.attachmentNames} hasAttachments={item.hasAttachments} />
      </span>
    </button>
  );
}
